import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiRefresh } from '@lumapps/lumx/icons';
import { Alignment, Button, Emphasis, FlexBox, Kind, Message, Orientation, Text } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { formatDate } from '@lumapps/utils/date/formatDate';

import { SCOPE, SEGMENT_DATA_IDS_MAPPING } from '../../constants';
import { SEGMENT } from '../../keys';
import { SegmentProvisioningInfo, SegmentProvisioningStatus } from '../../types';

import './SegmentSizeField.scss';

export interface SegmentSizeFieldProps {
    currentSize: number | null | undefined;
    onRefreshSize?: () => void;
    isDisabled?: boolean;
    lastUpdateAt?: string;
    isLoading?: boolean;
    canCalculateSize?: boolean;
    mode: 'create' | 'edit' | 'drawer' | 'popover';
    provisioningInfo?: SegmentProvisioningInfo;
    displayProvInfoMessage?: boolean;
}

export const SegmentSizeField = ({
    currentSize,
    onRefreshSize,
    lastUpdateAt,
    isDisabled,
    isLoading,
    canCalculateSize = true,
    mode,
    provisioningInfo,
    displayProvInfoMessage = true,
}: SegmentSizeFieldProps) => {
    const { translateAndReplace, pluralize, translateKey } = useTranslate();
    const { get } = useDataAttributes(`${SCOPE}-${SEGMENT_DATA_IDS_MAPPING[mode]}`);

    const actionLabelKey = React.useMemo(() => {
        if (isLoading) {
            return SEGMENT.CALCULATING_SEGMENT_SIZE;
        }

        if (currentSize !== 0 && !currentSize) {
            return SEGMENT.CALCULATE_SEGMENT_SIZE;
        }

        return GLOBAL.REFRESH;
    }, [isLoading, currentSize]);

    const wasComputed = Boolean(currentSize || currentSize === 0);

    const getProvisioningInfoText = () => {
        if (provisioningInfo?.lastProvisioningStatus === SegmentProvisioningStatus.RUNNING) {
            return translateKey(SEGMENT.DEPLOY_RUNNING);
        }
        if (provisioningInfo?.lastProvisionedAt) {
            return translateAndReplace(SEGMENT.SEGMENT_LAST_DEPLOYED_ON, {
                date: formatDate(provisioningInfo.lastProvisionedAt, 'LLL', true),
            });
        }

        return null;
    };

    return (
        <FlexBox
            {...get({
                element: 'size-field',
            })}
            orientation={mode === 'drawer' ? Orientation.vertical : Orientation.horizontal}
            vAlign={Alignment.left}
            hAlign="center"
            gap="regular"
        >
            {wasComputed && (
                <>
                    <Text as="p" typography="body1">
                        {translateAndReplace(pluralize(GLOBAL.NB_PEOPLE, currentSize as number), {
                            nb: currentSize as number,
                        })}
                    </Text>

                    <Text as="p" typography="body1" color="dark" colorVariant="L2">
                        {lastUpdateAt
                            ? translateAndReplace(SEGMENT.SEGMENT_LAST_UPDATED_ON, {
                                  date: formatDate(lastUpdateAt),
                              })
                            : null}
                    </Text>
                </>
            )}
            {provisioningInfo && (
                <Text as="p" typography="body1" color="dark" colorVariant="L2">
                    {getProvisioningInfoText()}
                </Text>
            )}
            {canCalculateSize && (
                <Button
                    size="s"
                    emphasis={Emphasis.medium}
                    isDisabled={isDisabled || isLoading}
                    leftIcon={mdiRefresh}
                    onClick={onRefreshSize}
                    {...get({
                        element: wasComputed ? 'refresh' : 'calculate',
                    })}
                >
                    {translateKey(actionLabelKey)}
                </Button>
            )}
            {displayProvInfoMessage && canCalculateSize && provisioningInfo?.lastProvisionedAt && (
                <Message kind={Kind.info}>{translateKey(SEGMENT.DEPLOY_INFORMATION)}</Message>
            )}
        </FlexBox>
    );
};
